import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { batchAPI } from '../../services/api';

const BatchForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    batch_name: '',
    subject: '',
    instructor: '',
    start_time: '',
    end_time: '',
    days_of_week: '',
    start_date: '',
    end_date: '',
    max_students: 30,
    fee_amount: '',
    description: '',
  });

  const [errors, setErrors] = useState({});

  // Fetch batch data if editing
  const { data: batch, isLoading: isLoadingBatch } = useQuery({
    queryKey: ['batch', id],
    queryFn: () => batchAPI.getById(id).then(res => res.data),
    enabled: isEditing,
  });

  // Load batch data when editing
  useEffect(() => {
    if (batch && isEditing) {
      setFormData({
        batch_name: batch.batch_name || '',
        subject: batch.subject || '',
        instructor: batch.instructor || '',
        start_time: batch.start_time || '',
        end_time: batch.end_time || '',
        days_of_week: batch.days_of_week || '',
        start_date: batch.start_date ? batch.start_date.split('T')[0] : '',
        end_date: batch.end_date ? batch.end_date.split('T')[0] : '',
        max_students: batch.max_students || 30,
        fee_amount: batch.fee_amount || '',
        description: batch.description || '',
      });
    }
  }, [batch, isEditing]);

  const createMutation = useMutation({
    mutationFn: (data) => batchAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['batches']);
      navigate('/batches');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data) => batchAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['batches']);
      queryClient.invalidateQueries(['batch', id]);
      navigate('/batches');
    },
  });

  const validateForm = () => {
    const newErrors = {};

    if (!formData.batch_name.trim()) {
      newErrors.batch_name = 'Batch name is required';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (formData.max_students < 1) {
      newErrors.max_students = 'Maximum students must be at least 1';
    }

    if (!formData.fee_amount || formData.fee_amount <= 0) {
      newErrors.fee_amount = 'Enrollment fee amount is required and must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      instructor: formData.instructor || null,
      start_time: formData.start_time || null,
      end_time: formData.end_time || null,
      days_of_week: formData.days_of_week || null,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      fee_amount: formData.fee_amount ? parseInt(formData.fee_amount) : null,
      description: formData.description || null,
    };

    if (isEditing) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const isLoading = isLoadingBatch || createMutation.isPending || updateMutation.isPending;

  if (isLoadingBatch) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/batches')}
          className="flex items-center text-gray-300 hover:text-gray-100 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Batches
        </button>
        <h1 className="text-3xl font-bold text-white">
          {isEditing ? 'Edit Batch' : 'Add New Batch'}
        </h1>
        <p className="text-gray-300 mt-2">
          {isEditing ? 'Update batch information' : 'Enter batch details to create a new batch'}
        </p>
      </div>

      {/* Form */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="batch_name" className="block text-sm font-medium text-gray-300 mb-2">
                Batch Name *
              </label>
              <input
                type="text"
                id="batch_name"
                name="batch_name"
                value={formData.batch_name}
                onChange={handleChange}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 bg-gray-700 text-white placeholder-gray-400 ${
                  errors.batch_name ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter batch name"
              />
              {errors.batch_name && (
                <p className="mt-1 text-sm text-red-400">{errors.batch_name}</p>
              )}
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                Subject *
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 bg-gray-700 text-white placeholder-gray-400 ${
                  errors.subject ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter subject"
              />
              {errors.subject && (
                <p className="mt-1 text-sm text-red-400">{errors.subject}</p>
              )}
            </div>
          </div>

          {/* Instructor */}
          <div>
            <label htmlFor="instructor" className="block text-sm font-medium text-gray-300 mb-2">
              Instructor
            </label>
            <input
              type="text"
              id="instructor"
              name="instructor"
              value={formData.instructor}
              onChange={handleChange}
              className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 bg-gray-700 text-white placeholder-gray-400"
              placeholder="Enter instructor name"
            />
          </div>

          {/* Schedule */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="start_time" className="block text-sm font-medium text-gray-300 mb-2">
                Start Time
              </label>
              <input
                type="time"
                id="start_time"
                name="start_time"
                value={formData.start_time}
                onChange={handleChange}
                className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 bg-gray-700 text-white"
              />
            </div>

            <div>
              <label htmlFor="end_time" className="block text-sm font-medium text-gray-300 mb-2">
                End Time
              </label>
              <input
                type="time"
                id="end_time"
                name="end_time"
                value={formData.end_time}
                onChange={handleChange}
                className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 bg-gray-700 text-white"
              />
            </div>

            <div>
              <label htmlFor="days_of_week" className="block text-sm font-medium text-gray-300 mb-2">
                Days of Week
              </label>
              <input
                type="text"
                id="days_of_week"
                name="days_of_week"
                value={formData.days_of_week}
                onChange={handleChange}
                className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 bg-gray-700 text-white placeholder-gray-400"
                placeholder="e.g., Monday, Wednesday, Friday"
              />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-300 mb-2">
                Start Date
              </label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 bg-gray-700 text-white"
              />
            </div>

            <div>
              <label htmlFor="end_date" className="block text-sm font-medium text-gray-300 mb-2">
                End Date
              </label>
              <input
                type="date"
                id="end_date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 bg-gray-700 text-white"
              />
            </div>
          </div>

          {/* Capacity and Fee */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="max_students" className="block text-sm font-medium text-gray-300 mb-2">
                Maximum Students *
              </label>
              <input
                type="number"
                id="max_students"
                name="max_students"
                value={formData.max_students}
                onChange={handleChange}
                min="1"
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 bg-gray-700 text-white ${
                  errors.max_students ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.max_students && (
                <p className="mt-1 text-sm text-red-400">{errors.max_students}</p>
              )}
            </div>

            <div>
              <label htmlFor="fee_amount" className="block text-sm font-medium text-gray-300 mb-2">
                Enrollment Fee Amount (₹) *
              </label>
              <input
                type="number"
                id="fee_amount"
                name="fee_amount"
                value={formData.fee_amount}
                onChange={handleChange}
                min="0"
                step="0.01"
                required
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 bg-gray-700 text-white placeholder-gray-400 ${
                  errors.fee_amount ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter enrollment fee amount"
              />
              <p className="mt-1 text-sm text-gray-400">
                This fee will be automatically charged when students enroll in this batch
              </p>
              {errors.fee_amount && (
                <p className="mt-1 text-sm text-red-400">{errors.fee_amount}</p>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 bg-gray-700 text-white placeholder-gray-400"
              placeholder="Enter batch description"
            />
          </div>

          {/* Error Messages */}
          {(createMutation.error || updateMutation.error) && (
            <div className="bg-red-900/20 border border-red-700 rounded-md p-4">
              <p className="text-sm text-red-300">
                {createMutation.error?.response?.data?.detail || 
                 updateMutation.error?.response?.data?.detail || 
                 'An error occurred. Please try again.'}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/batches')}
              className="px-4 py-2 border border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 focus:ring-offset-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 focus:ring-offset-gray-800 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Update Batch' : 'Create Batch')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BatchForm; 